import { text, integer, index, uniqueIndex } from "drizzle-orm/sqlite-core";
import { defineTable, crossModuleFk } from "@kubdee/sdk/schema";
import { marketingContentPosts } from "./content-posts";

// ============================================================================
// marketing_post_targets — Per-channel fan-out of a content post
//
// One row per (post, connector instance). A post targeted at several social
// accounts produces one row per destination; each tracks its own publish
// state machine + provider response. Captures provider-neutral metadata only:
//   - `provider` is the capability slug (e.g. `facebook-page`, `instagram-feed`)
//   - `provider_account_id` is the page/account id within the provider
//   - `provider_post_id` / `provider_url` are filled in after a successful
//     publish via the connectors adapter
//
// `connector_instance_id` is a cross-module reference to
// `connectors.instances.id` — declared via the SDK's `crossModuleFk` helper
// (see migration 001) and intentionally NOT a Drizzle `references()` so the
// marketing module stays Tier-2 isolated from the connectors schema. Runtime
// integrity is enforced by the connector dispatcher (see #790).
// ============================================================================

// Cross-module FK target — resolved at module load. Used by the migration to
// emit the right FOREIGN KEY clause and by future runtime integrity checks.
// Importing the helper here does NOT pull in the connectors schema — the
// returned value is just `{ tableName: "connectors_instances", columnName: "id" }`.
export const connectorInstanceFk = crossModuleFk("connectors", "instances");

export const marketingPostTargets = defineTable(
  "marketing",
  "post_targets",
  {
    id: text("id").primaryKey(), // ULID
    companyId: text("company_id").notNull(),
    postId: text("post_id")
      .notNull()
      .references(() => marketingContentPosts.id, { onDelete: "cascade" }),
    // Cross-module reference to connectors.instances.id — see file header.
    connectorInstanceId: text("connector_instance_id").notNull(),
    // Capability-neutral provider slug (e.g. `facebook-page`, `instagram-feed`,
    // `tiktok-video`). The connector adapter resolves the slug to the actual
    // HTTP transport at publish time.
    provider: text("provider").notNull(),
    // Provider-assigned page/account id (for example page id, business
    // account id, company page id, or user/account id).
    providerAccountId: text("provider_account_id").notNull(),
    // Granular per-target state. See `services/status-transitions.ts`.
    status: text("status").notNull().default("draft"),
    // Provider-side artefacts filled after publish.
    providerPostId: text("provider_post_id"),
    providerUrl: text("provider_url"),
    // Error tracking (filled when status flips to `failed`).
    errorCode: text("error_code"),
    errorMessage: text("error_message"),
    attempts: integer("attempts").notNull().default(0),
    lastAttemptedAt: text("last_attempted_at"),
    publishedAt: text("published_at"),
    // Idempotency key — caller-supplied or auto-generated; protects against
    // double-publish on retries. Unique per company (see uniqueIndex below).
    idempotencyKey: text("idempotency_key"),
    // Audit columns
    createdAt: text("created_at").notNull(),
    createdBy: text("created_by"),
    updatedAt: text("updated_at").notNull(),
    updatedBy: text("updated_by"),
    deletedAt: text("deleted_at"),
    deletedBy: text("deleted_by"),
  },
  (table) => [
    index("marketing_post_targets_company_id_idx").on(table.companyId),
    index("marketing_post_targets_company_status_idx").on(
      table.companyId,
      table.status,
    ),
    index("marketing_post_targets_post_id_idx").on(table.postId),
    index("marketing_post_targets_connector_instance_id_idx").on(
      table.connectorInstanceId,
    ),
    // Idempotency keys must be unique per company. Multiple NULLs are allowed
    // by SQLite (treated as distinct), which matches our semantics — only
    // caller-supplied keys participate in the dedupe.
    uniqueIndex("marketing_post_targets_company_idempotency_unique").on(
      table.companyId,
      table.idempotencyKey,
    ),
  ],
);

export type MarketingPostTarget = typeof marketingPostTargets.$inferSelect;
export type NewMarketingPostTarget = typeof marketingPostTargets.$inferInsert;
